import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';

const ChatPanel = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'system',
      message: 'Consultation started. All messages are encrypted and will be included in the consultation transcript.',
      timestamp: new Date(),
      type: 'system'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const message = {
      id: messages.length + 1,
      sender: 'lawyer',
      message: newMessage.trim(),
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');
    
    // Simulate client response (for demo purposes)
    if (messages.length < 10) {
      setTimeout(() => {
        const clientResponse = {
          id: messages.length + 2,
          sender: 'client',
          message: 'Thank you for the clarification. I understand better now.',
          timestamp: new Date(),
          type: 'text'
        };
        setMessages(prev => [...prev, clientResponse]);
      }, 1500);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-KE', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const MessageBubble = ({ message }) => {
    const isLawyer = message.sender === 'lawyer';
    const isSystem = message.sender === 'system';

    if (isSystem) {
      return (
        <div className="flex justify-center my-4">
          <div className="bg-secondary-100 text-text-secondary px-3 py-2 rounded-lg text-xs max-w-xs text-center">
            <Icon name="Shield" size={12} className="inline mr-1" />
            {message.message}
          </div>
        </div>
      );
    }

    return (
      <div className={`flex mb-4 ${isLawyer ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-xs lg:max-w-md ${isLawyer ? 'order-2' : 'order-1'}`}>
          <div
            className={`px-4 py-2 rounded-lg ${
              isLawyer
                ? 'bg-primary text-white rounded-br-sm' :'bg-secondary-100 text-text-primary rounded-bl-sm'
            }`}
          >
            <p className="text-sm">{message.message}</p>
          </div>
          <div className={`text-xs text-text-secondary mt-1 ${isLawyer ? 'text-right' : 'text-left'}`}>
            {formatTime(message.timestamp)}
          </div>
        </div>
        
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isLawyer ? 'order-1 mr-3 bg-primary' : 'order-2 ml-3 bg-secondary-200'
        }`}>
          <Icon 
            name="User" 
            size={16} 
            color={isLawyer ? 'white' : undefined}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-text-primary">Consultation Chat</h3>
          <div className="flex items-center space-x-1 text-xs text-text-secondary">
            <Icon name="Shield" size={12} />
            <span>Encrypted</span>
          </div>
        </div>
        <p className="text-xs text-text-secondary mt-1">
          Messages are automatically included in consultation transcript
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-border">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              rows="2"
              className="w-full px-3 py-2 border border-border rounded-interactive resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="px-4 py-2 bg-primary hover:bg-primary-dark disabled:bg-secondary-200 disabled:text-text-secondary text-white rounded-interactive transition-smooth self-end"
          >
            <Icon name="Send" size={16} />
          </button>
        </form>
        
        <div className="flex items-center justify-between mt-2 text-xs text-text-secondary">
          <span>Press Enter to send, Shift+Enter for new line</span>
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={10} />
            <span>Auto-save enabled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;